import { useState, useEffect } from 'react'
import { get, set, cloneDeep } from 'lodash'
import 'src/styles/components/common/orgTree.less'

type Node = {
  id: string,
  label: string,
  expand: string,
  children: string
}
type Props = {
  // 树数据
  data: any,
  // 节点配置数据源别名
  node: Node,
  // 是否横向展开
  horizontal: boolean,
  // 是否可折叠/展开
  collapsable: boolean,
  // 默认展开层级，true 展开全部，false 收起全部，数字则代表展开层级
  expandLevel: boolean | number,
  // 节点宽度
  labelWidth?: number,
  // 节点样式类
  labelClassName?: string,
  // 自定义渲染节点
  renderContent?: Function,
  // 点击回调
  onClick?: Function,
  // 展开回调
  onExpand?: Function
}

const OrgTree = (props: Props) => {
  const [treeData, setTreeData] = useState()

  useEffect(() => {
    const checkExpand = (data: any, deep = 1) => {
      const { expandLevel, node } = props
      if (expandLevel === 0 || expandLevel === true || deep < Number(expandLevel)) {
        // 为树数据附上 node.expand 是否展开标记
        const setExpand = (tree: any) => {
          const expandStatus = get(tree, node.expand)
          // 如果初始数据中有配默认 expand ，则保留默认配置
          set(tree, node.expand, expandStatus !== undefined ? !!expandStatus : true)
          // 便利树节点
          const childrenList = get(tree, node.children)
          childrenList && checkExpand(childrenList, deep + 1)
        }

        Array.isArray(data) ? data.forEach(item => setExpand(item)) : setExpand(data)
      }
    }

    const { data, expandLevel } = props
    const copyData = cloneDeep(data)
    expandLevel !== false && checkExpand(copyData)
    setTreeData(copyData)
  }, [props.data, props.node, props.expandLevel])

  // 创建 node 节点，包括 content 节点和 children 节点
  const renderNode = (data: any, index?: number) => {
    const { node, collapsable, renderContent, labelWidth, labelClassName, onClick, onExpand } = props
    const nodeId = get(data, node.id)
    const nodeLabel = get(data, node.label)
    const nodeExpand = get(data, node.expand)
    const nodeChildren = get(data, node.children)
    // 是否叶子节点
    const nodeIsLeaf = !(Array.isArray(nodeChildren) && nodeChildren.length)

    // 创建 children 子节点
    const renderChildrenNode = (list: any[]) => {
      if (!nodeIsLeaf) {
        return (
          <div
            key={`${nodeId}_child`}
            className='org-tree-node-children'
          >
            {list.map((item: any, index: number) => renderNode(item, index))}
          </div>
        )
      }
      return null
    }

    // 创建 content 节点，包括 展开/折叠 按钮
    const renderContentNode = () => {
      // 节点单击事件
      const handleClick = (e: any) => {
        onClick?.(e, data)
      }

      // 将子树折叠
      const collapse = (list: any[]) => {
        list.forEach(child => {
          set(child, node.expand, false)
          const childrenList = get(child, node.children)
          Array.isArray(childrenList) && collapse(childrenList)
        })
      }

      // 展开/折叠事件
      const handleExpand = (e: any) => {
        e.stopPropagation()
        // 为树数据附上 node.expand 是否展开标记
        set(data, node.expand, !nodeExpand)
        // 展开的节点将其收起
        nodeExpand && nodeChildren && collapse(nodeChildren)
        setTreeData(prev => Object.assign({}, prev))
        onExpand?.(e, data)
      }

      // 添加自定义渲染的节点组件
      const childNodes = []
      if (typeof renderContent === 'function') {
        const vnode = renderContent(cloneDeep(data))
        vnode && childNodes.push(vnode)
      } else {
        childNodes.push(<div key={`${nodeId}_label`}>{nodeLabel}</div>)
      }

      // 非叶子节点渲染 展开/折叠 按钮
      if (collapsable && !nodeIsLeaf) {
        const cls = `org-tree-node-btn ${nodeExpand ? 'expanded' : ''}`
        childNodes.push(<span key={`${nodeId}_expanded`} onClick={handleExpand} className={cls} />)
      }

      const cls = `org-tree-node-label-inner ${labelClassName || ''}`
      const style = labelWidth ? { width: labelWidth } : {}
      return (
        <div key={`${nodeId}_node`} onClick={handleClick} className='org-tree-node-label'>
          <div className={cls} style={style}>{childNodes}</div>
        </div>
      )
    }

    // 创建根、子节点
    const childNodes: any = [renderContentNode()]
    const collapsed = collapsable && !nodeExpand
    !collapsed && childNodes.push(renderChildrenNode(nodeChildren))

    return (
      <div
        key={`${nodeId}_${index}`}
        className={`org-tree-node ${nodeIsLeaf ? 'is-leaf' : collapsed ? 'collapsed' : ''}`}
      >{childNodes}</div>
    )
  }

  if (!treeData) return null

  const { horizontal } = props
  return (
    <div className={`org-tree ${horizontal ? 'horizontal' : ''}`}>
      {renderNode(treeData)}
    </div>
  )
}

OrgTree.defaultProps = {
  node: {
    id: 'Node.ID',
    label: 'Node.NAME',
    expand: 'Expand',
    children: 'Children'
  },
  horizontal: false,
  collapsable: true,
  expandLevel: true
}

export default OrgTree
